/**
 * Audit — query/operation history viewer.
 *
 * Admin: full fleet history, with filters (incl. by user) + retention pruning.
 * Non-admin (with the canViewOwnAudit user flag): SCOPED TO OWN ACTIVITY ONLY.
 * The backend force-scopes /api/history to the caller's user_id when they're
 * not an admin (and ignores any client-supplied userId), so the by-user
 * dropdown + prune controls are hidden here for non-admins; everything else
 * (search, kind, success, db, dates, paging) works the same.
 */
import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { api } from '../lib/api.js';
import { currentUser } from '../lib/auth-store.js';
import { listAuditHistory, pruneHistory } from '../lib/history.js';
import { toast } from '../lib/toast.js';
import SqlViewerModal from '../components/SqlViewerModal.jsx';

// Grouped kind filter. Mirrors the `Kind:` values the backend writes to
// query_history (internal/api/*.go logHistory calls); keep in sync when a new
// logged kind is added. The filter matches a kind exactly, so these must be the
// literal stored strings.
const KIND_GROUPS = [
  { label: 'Queries',           kinds: ['query', 'insert', 'update', 'delete', 'commit', 'rollback'] },
  { label: 'Schema & objects',  kinds: ['schema', 'routine', 'table-action', 'column-rename', 'object-copy', 'object-rename', 'drop-database', 'index-drop', 'table-maintain', 'structure-sync'] },
  { label: 'Data',              kinds: ['import', 'export', 'backup', 'restore', 'mock-data', 'refsync', 'bulk-sql'] },
  { label: 'Users & access',    kinds: ['mysql-user', 'grant', 'auth-logout-all'] },
  { label: 'Server variables',  kinds: ['server-variable', 'server-variable-manage', 'server-variable-unmanage', 'server-variable-reapply'] },
  { label: 'MCP / AI',          kinds: ['mcp-list', 'mcp-query', 'mcp-write-proposed', 'mcp-write', 'mcp-write-rejected', 'mcp-backup', 'mcp-restore-proposed', 'mcp-restore', 'mcp-restore-rejected', 'mcp-dsl-proposed', 'mcp-dsl', 'mcp-dsl-rejected', 'ai-bypass-config', 'ai-ttl-config'] },
  { label: 'Forms',             kinds: ['forms-dsl-action'] },
  { label: 'Analysis',          kinds: ['sp-debug', 'query-profile', 'query-compare'] },
  { label: 'Connectors',        kinds: ['connector-create', 'connector-rotate', 'connector-revoke', 'connector-unrevoke', 'connector-delete', 'connector-download', 'connector-grant'] },
  { label: 'Ninocat Link',      kinds: ['link-download', 'link-revoke', 'link-connect', 'link-disconnect', 'link-forward'] },
  { label: 'Monitor',           kinds: ['kill'] },
];

const PAGE = 100;

export default function Audit() {
  const [filters, setFilters] = createStore({ q: '', kind: '', success: '', userId: '', database: '', from: '', to: '', offset: 0 });
  const [pruneDays, setPruneDays] = createSignal(90);
  const [viewSql, setViewSql] = createSignal(null); // { sql, title } for the SQL modal

  // Admin gets full controls + the by-user dropdown + the prune button.
  // A non-admin with canViewOwnAudit gets the same view but force-scoped to
  // their own user_id by the backend; the admin-only controls are hidden.
  const isAdmin = createMemo(() => currentUser()?.role === 'admin');

  // Users for the "by user" filter dropdown — admin only (the /api/users
  // endpoint requires admin/sub-admin and is irrelevant when own-scoped).
  const [users] = createResource(
    () => isAdmin(),
    async (yes) => {
      if (!yes) return [];
      try { return (await api.get('/api/users')).users ?? []; } catch { return []; }
    },
  );

  // A local `YYYY-MM-DD` (from a <input type="date">) → unix seconds at local
  // midnight. `endOfDay` shifts to the NEXT midnight so `before` (created_at <
  // before) makes the chosen "to" day inclusive.
  const dayToUnix = (s, endOfDay = false) => {
    if (!s) return 0;
    const t = new Date(s + 'T00:00:00').getTime();
    if (Number.isNaN(t)) return 0;
    return Math.floor(t / 1000) + (endOfDay ? 86400 : 0);
  };

  const [data, { refetch }] = createResource(
    () => ({
      q: filters.q, kind: filters.kind, success: filters.success,
      userId: filters.userId, database: filters.database, offset: filters.offset,
      after: dayToUnix(filters.from), before: dayToUnix(filters.to, true),
    }),
    (f) => listAuditHistory({ ...f, limit: PAGE }).catch((e) => {
      toast.error('Load audit: ' + e.message);
      return { entries: [], total: 0 };
    }),
  );

  const entries = () => data()?.entries ?? [];
  const total = () => data()?.total ?? 0;
  // Distinct databases on the current page — datalist suggestions for the filter
  // (best-effort; the actual match is a server-side substring, so any name works).
  const dbOptions = () => [...new Set(entries().map((e) => e.database).filter(Boolean))].sort();

  // Editing a filter resets paging to the first page.
  const setFilter = (k, v) => { setFilters({ [k]: v, offset: 0 }); };
  const clearDates = () => setFilters({ from: '', to: '', offset: 0 });

  const prune = async () => {
    const days = pruneDays();
    if (!days || days <= 0) return;
    if (!confirm(`Delete all audit entries older than ${days} days? This cannot be undone.`)) return;
    try {
      const r = await pruneHistory(days);
      toast.success(`Pruned ${r.deleted} entr${r.deleted === 1 ? 'y' : 'ies'}`);
      refetch();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const fmtTime = (sec) => new Date(sec * 1000).toLocaleString();

  return (
    <div class="audit-page">
      <div class="audit-toolbar">
        <input class="input audit-search" placeholder="Search SQL…"
               value={filters.q} onInput={(e) => setFilter('q', e.target.value)} />
        <select class="input" value={filters.kind} onChange={(e) => setFilter('kind', e.target.value)}>
          <option value="">All kinds</option>
          <For each={KIND_GROUPS}>{(g) =>
            <optgroup label={g.label}>
              <For each={g.kinds}>{(k) => <option value={k}>{k}</option>}</For>
            </optgroup>
          }</For>
        </select>
        <select class="input" value={filters.success} onChange={(e) => setFilter('success', e.target.value)}>
          <option value="">All</option>
          <option value="1">Success</option>
          <option value="0">Failed</option>
        </select>
        <Show when={isAdmin()}>
          <select class="input" value={filters.userId} onChange={(e) => setFilter('userId', e.target.value)} title="Filter by user">
            <option value="">All users</option>
            <For each={users() ?? []}>{(u) => <option value={u.id}>{u.username}</option>}</For>
          </select>
        </Show>
        <Show when={!isAdmin()}>
          <span class="muted"
                title={currentUser()?.role === 'subadmin'
                  ? 'Your own activity, plus what User/Read-only accounts did on the connections shared to your tags'
                  : 'Audit access is scoped to your own activity'}>
            {currentUser()?.role === 'subadmin' ? "your activity + your team's connections" : 'your activity only'}
          </span>
        </Show>
        <input class="input audit-dbfilter" placeholder="Database…" list="audit-db-list"
               title="Filter by database (substring)"
               value={filters.database} onInput={(e) => setFilter('database', e.target.value)} />
        <datalist id="audit-db-list">
          <For each={dbOptions()}>{(d) => <option value={d} />}</For>
        </datalist>
        <span class="audit-daterange" title="Filter by date (inclusive)">
          <input class="input" type="date" value={filters.from}
                 max={filters.to || undefined}
                 onInput={(e) => setFilter('from', e.target.value)} />
          <span class="muted">→</span>
          <input class="input" type="date" value={filters.to}
                 min={filters.from || undefined}
                 onInput={(e) => setFilter('to', e.target.value)} />
          <Show when={filters.from || filters.to}>
            <button class="btn btn-sm btn-ghost" title="Clear dates" onClick={clearDates}>×</button>
          </Show>
        </span>
        <button class="btn btn-sm btn-ghost" onClick={() => refetch()} disabled={data.loading}>⟳ Refresh</button>
        <div class="spacer" />
        <Show when={isAdmin()}>
          <span class="audit-prune">
            Prune older than
            <input class="input mono" type="number" min="1" style="width:64px;"
                   value={pruneDays()} onInput={(e) => setPruneDays(parseInt(e.target.value, 10) || 0)} />
            days
            <button class="btn btn-sm" onClick={prune}>Prune</button>
          </span>
        </Show>
      </div>

      <div class="audit-table-wrap">
        <table class="audit-table">
          <thead>
            <tr>
              <th>Time</th><th>User</th><th>Connection</th><th>Database</th>
              <th>Kind</th><th>SQL</th><th class="num">Rows</th><th>Status</th><th class="num">ms</th>
            </tr>
          </thead>
          <tbody>
            <Show when={!data.loading} fallback={<tr><td colspan="9" class="muted" style="padding:16px;">Loading…</td></tr>}>
              <Show when={entries().length === 0}>
                <tr><td colspan="9" class="muted" style="padding:16px;">No matching entries.</td></tr>
              </Show>
              <For each={entries()}>
                {(e) => (
                  <tr classList={{ 'audit-failed': !e.success }}>
                    <td class="mono nowrap">{fmtTime(e.createdAt)}</td>
                    <td>{e.username || <span class="muted">—</span>}</td>
                    <td>{e.connectionName || <span class="muted">—</span>}</td>
                    <td class="mono">{e.database || <span class="muted">—</span>}</td>
                    <td><span class="audit-kind">{e.kind}</span></td>
                    <Show when={e.sqlText}
                          fallback={<td class="mono audit-sql muted">—</td>}>
                      <td class="mono audit-sql audit-sql-click"
                          title={`${e.sqlText}\n\n(click to open)`}
                          onClick={() => setViewSql({
                            sql: e.sqlText,
                            title: `${e.kind} · ${e.username || '—'} · ${fmtTime(e.createdAt)}`,
                          })}>
                        {e.sqlText}
                      </td>
                    </Show>
                    <td class="num mono">{e.rowsAffected}</td>
                    <td><span class={e.success ? 'hist-ok' : 'hist-err'} title={e.error}>{e.success ? '✓' : '✕'}</span></td>
                    <td class="num mono">{e.durationMs}</td>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>

      <div class="audit-footer">
        <span class="muted mono">
          {total() === 0 ? '0' : `${filters.offset + 1}–${Math.min(filters.offset + PAGE, total())}`} of {total()}
        </span>
        <div class="spacer" />
        <button class="btn btn-sm btn-ghost" disabled={filters.offset === 0}
                onClick={() => setFilters('offset', Math.max(0, filters.offset - PAGE))}>← Prev</button>
        <button class="btn btn-sm btn-ghost" disabled={filters.offset + PAGE >= total()}
                onClick={() => setFilters('offset', filters.offset + PAGE)}>Next →</button>
      </div>

      <SqlViewerModal
        open={!!viewSql()}
        sql={viewSql()?.sql || ''}
        title={viewSql()?.title || 'SQL'}
        onClose={() => setViewSql(null)}
      />
    </div>
  );
}
